// lib
import { append, concat, filter, find, sort, pipe } from "ramda";
// src
import { MY_PLANNING_ID, PresetType, ResourceType, FETCHABLE_RESOURCES } from "config/constants";
import i18n, { selfTranslate } from "i18n/proxy";
import { capitalizeFirstLetter } from "utils/string";
import { isInstantId } from "utils/presets";

// Rows/groups processing

const enhanceRows = ({ courseIndex, groupIndex, peopleIndex, roomIndex }) => (rows) =>
  rows.map((r) => {
    if (r.resourceType === ResourceType.PERSON) {
      if (r.id === MY_PLANNING_ID) return { ...r, content: selfTranslate(r.name) };
      const fromIndex = peopleIndex[r.id];
      if (fromIndex) {
        const { name } = fromIndex;
        return { ...r, content: name };
      }
    }
    if (r.resourceType === ResourceType.ROOM) {
      const fromIndex = roomIndex[r.id];
      if (fromIndex) {
        const { name, mapwizeLink, id, ...rest } = fromIndex;
        const content = mapwizeLink
          ? `<a href=${mapwizeLink} target="_blank" rel="noopener noreferrer"><i class="place"></i>${name}</a>`
          : name;
        return {
          ...r,
          ...rest,
          content,
          name,
        };
      }
    }
    if (r.resourceType === ResourceType.GROUP) {
      const fromIndex = groupIndex[r.id];
      if (fromIndex) {
        const { display: content } = fromIndex;
        return { ...r, content };
      }
    }
    if (r.resourceType === ResourceType.COURSE) {
      const fromIndex = courseIndex[r.id];
      if (fromIndex) {
        const { display } = fromIndex;
        return { ...r, content: display };
      }
    }
    if (r.name) {
      return { ...r, content: r.name };
    }
    return r;
  });

const sortRows = (
  { id: idA, content: contentA, name: nameA },
  { id: idB, content: contentB, name: nameB },
) => {
  // special case for my planning
  if (idA === MY_PLANNING_ID) return -1;
  if (idB === MY_PLANNING_ID) return 1;
  // sort by name (useful for rooms whose content is an HTML link)
  if (nameA && nameB) return nameA.toLowerCase() < nameB.toLowerCase() ? -1 : 1;
  // sort by content
  return contentA.toLowerCase() < contentB.toLowerCase() ? -1 : 1;
};

const processRows = (indexes, rows) =>
  pipe(
    enhanceRows(indexes),
    filter(({ content }) => Boolean(content)),
    sort(sortRows),
  )(rows);

const generateTopNests = (rows) => {
  const miscNest = {
    id: "misc",
    content: capitalizeFirstLetter(i18n.t("common.misc")),
    presetType: PresetType.INSTANT,
    nestedGroups: [],
  };
  const fetchableResourceNests = FETCHABLE_RESOURCES.map(({ singular, plural }) => ({
    id: plural,
    content: capitalizeFirstLetter(i18n.t(`resources.${plural}`)),
    resourceType: ResourceType[singular.toUpperCase()],
    nestedGroups: [],
  }));
  // people are not fetchable resources
  const peopleNest = {
    id: "people",
    content: capitalizeFirstLetter(i18n.t("resources.people")),
    resourceType: ResourceType.PERSON,
    nestedGroups: [],
  };
  // ordering matters (misc is a catch-all nest), needs to be changed if display order has to be different
  const nests = [peopleNest, ...fetchableResourceNests, miscNest];
  rows.forEach((r) => {
    const nest = find((n) => {
      if (r.notInTopNest) return false;
      if (r.presetType && r.presetType === n.presetType) return true;
      if (r.resourceType && r.resourceType === n.resourceType) return true;
      return false;
    }, nests);
    if (nest) nest.nestedGroups.push(r.id);
  });
  return filter((n) => n.nestedGroups.length, nests);
};

const addChildrenIfCourse = ({ courseIndex }, rows) =>
  rows.reduce((acc, r) => {
    if (r.resourceType !== ResourceType.COURSE) {
      return append(r, acc);
    }
    const fromIndex = courseIndex[r.id];
    if (!fromIndex) return append(r, acc);
    const { children } = fromIndex;
    if (children && children.length) {
      const parentRow = {
        ...r,
        nestedGroups: children.map(({ id }) => `${id}`),
      };
      const childrenRows = children.map(({ id }) => ({
        id: `${id}`,
        resourceType: ResourceType.COURSE,
        className: "nested-course",
        notInTopNest: true,
      }));
      return concat(append(parentRow, childrenRows), acc);
    }
    return append(r, acc);
  }, []);

export const generateRows = (indexes, rows) => {
  const allRows = addChildrenIfCourse(indexes, rows);
  const leaves = processRows(indexes, allRows);
  const topNests = generateTopNests(leaves);
  return concat(topNests, leaves);
};

// Events preprocessing
const unfoldPerRow = (events) =>
  events.reduce((acc, current) => {
    if (current.presetId === MY_PLANNING_ID) {
      acc.push({ ...current, group: MY_PLANNING_ID });
    } else if (current.stacked) {
      acc.push({
        ...current,
        id: `${current.id}.preset-${current.presetId}`,
        group: current.presetId,
      });
    } else if (isInstantId(current.presetId)) {
      acc.push({
        ...current,
        id: `${current.id}.preset-${current.presetId}`,
        group: current.presetId,
      });
    } else {
      // Explanation about the "group" key: it's a "timeline group" meaning it's a row (and not a Geode group)
      // Possible weakness if resource.id clash among different types (group/course...), could be prefixed
      // but then enhanceRows has to be modified (see usage of peopleIndex for instance)
      // OPTIM: could be optimized: duplication of events only for existing timeline group/rows
      const unfolds = ["rooms", "extraRooms", "groups", "courses"];
      unfolds.forEach((resourceType) => {
        if (current[resourceType] && current[resourceType].length) {
          current[resourceType].forEach((resource) => {
            // update id to avoid key duplicates + add group
            acc.push({
              ...current,
              id: `${current.id}.${resourceType}-${resource.id}`,
              group: resource.id,
            });
          });
        }
      });
      const peopleUnfolds = ["teachers", "optionalAttendees", "mandatoryAttendees"];
      if (current.author && current.author.id) {
        acc.push({
          ...current,
          id: `${current.id}.people-${current.author.id}`,
          group: current.author.id,
        });
      }
      peopleUnfolds.forEach((field) => {
        if (current[field] && current[field].length) {
          current[field].forEach((resource) => {
            // update id to avoid key duplicates + add group
            acc.push({
              ...current,
              id: `${current.id}.people-${resource.id}`,
              group: resource.id,
            });
          });
        }
      });
    }
    return acc;
  }, []);

const dedup = (events) => {
  const map = new Map();
  events.forEach((e) => map.set(e.id, e));
  return Array.from(map.values());
};

export const processEvents = (events) => dedup(unfoldPerRow(events));
